/**
 * iGeek Megastore — Unified Custom PC Builder Controller & PDF Engine
 * 
 * Features:
 * - 6-Core Slot Requirement & In-Stock Validation for Add to Cart
 * - Sold-out disabling & In-stock-first filtering
 * - Strict Case vs Cooler filtering
 * - Share build encoding/decoding & automatic URL restoration
 * - Core Hardware Compatibility & technician clearance advisory
 * - PSU Headroom Warning vs hard blocking & undersized PSU filtering
 * - Full modal keyboard accessibility (Escape, focus management, Tab focus trap, aria-labelledby)
 */

(function () {
  'use strict';

  /* ==========================================================================
     1. HARDWARE COMPATIBILITY & SPEC INFERENCE ENGINE
     ========================================================================== */

  const norm = v => (v == null ? '' : String(v).trim());
  const upper = v => norm(v).toUpperCase().replace(/\s+/g, ' ');

  const shortName = it => (it?.t || '')
    .replace(/\s*[-–—]\s*(Graphics Card|Motherboard|Power Supply|Gaming Case|Desktop Memory|RAM|Processors?).*$/i, '')
    .slice(0, 50);

  function inferSocket(it) {
    if (!it) return null;
    const rawSock = upper(it.s?.socket || '');
    if (rawSock.includes('AM5')) return 'AM5';
    if (rawSock.includes('AM4')) return 'AM4';
    if (rawSock.includes('1851')) return 'LGA1851';
    if (rawSock.includes('1700')) return 'LGA1700';
    if (rawSock.includes('1200')) return 'LGA1200';
    if (rawSock.includes('1151')) return 'LGA1151';

    const text = upper(`${it.t || ''} ${it.s?.chipset || ''} ${(it.tags || []).join(' ')}`);

    if (/\bAM5\b|SOCKET AM5/i.test(text)) return 'AM5';
    if (/\bAM4\b|SOCKET AM4/i.test(text)) return 'AM4';
    if (/\b1851\b|LGA\s*1851/i.test(text)) return 'LGA1851';
    if (/\b1700\b|LGA\s*1700/i.test(text)) return 'LGA1700';
    if (/\b1200\b|LGA\s*1200/i.test(text)) return 'LGA1200';

    if (/\b(X870E|X870|B850|X670E|X670|B650E|B650|A620)\b/i.test(text)) return 'AM5';
    if (/\b(X570|B550|A520|X470|B450|A320|X370|B350)\b/i.test(text)) return 'AM4';
    if (/\b(Z890|B860|H810)\b/i.test(text)) return 'LGA1851';
    if (/\b(Z790|B760|H770|H610|Z690|B660|H670)\b/i.test(text)) return 'LGA1700';
    if (/\b(Z590|B560|H570|H510|Z490|B460|H410)\b/i.test(text)) return 'LGA1200';

    if (/RYZEN\s*(9\s*9\d{3}|7\s*9\d{3}|5\s*9\d{3}|9\s*8\d{3}|7\s*8\d{3}|5\s*8\d{3}|9\s*7\d{3}|7\s*7\d{3}|5\s*7\d{3}|7500F|8600G|8700G|9800X3D|7800X3D|7950X3D|7900X3D)/i.test(text)) return 'AM5';
    if (/RYZEN\s*(9\s*5\d{3}|7\s*5\d{3}|5\s*5\d{3}|3\s*5\d{3}|5600|5700|5800|5900|5950|5600G|5700G|5700X3D|5800X3D|3600|3700|3800|3900|2600|1600)/i.test(text)) return 'AM4';
    if (/ULTRA\s*(5|7|9)\s*(2\d{2}|SERIES\s*2)/i.test(text) || /\b(245K|265K|285K|225F|235|245KF|265KF|285KF)\b/i.test(text)) return 'LGA1851';
    if (/1[234]\d{2}0(K|KF|F|KS|T)?\b|14TH\s*GEN|13TH\s*GEN|12TH\s*GEN/i.test(text)) return 'LGA1700';
    if (/1[01]\d{2}0(K|KF|F)?\b|11TH\s*GEN|10TH\s*GEN/i.test(text)) return 'LGA1200';

    return null;
  }

  function inferRamType(it) {
    if (!it) return null;
    const rawRam = upper(it.s?.ram_type || '');
    if (rawRam.includes('DDR5') || rawRam === 'D5') return 'DDR5';
    if (rawRam.includes('DDR4') || rawRam === 'D4') return 'DDR4';

    const text = upper(`${it.t || ''} ${it.s?.chipset || ''} ${(it.tags || []).join(' ')}`);

    if (/\bDDR5\b|\bUDIMM DDR5\b|\b(4800|5200|5600|6000|6200|6400|6600|6800|7000|7200|7400|7600|8000|8200|8400)\s*(MHZ|MT\/S|HZ)?\b/i.test(text)) {
      return 'DDR5';
    }
    if (/\bDDR4\b|\bUDIMM DDR4\b|\b(2133|2400|2666|3000|3200|3600|4000|4266|4400)\s*(MHZ|MT\/S|HZ)?\b/i.test(text)) {
      return 'DDR4';
    }

    const socket = inferSocket(it);
    if (socket === 'AM5' || socket === 'LGA1851') return 'DDR5';
    if (socket === 'AM4' || socket === 'LGA1200' || socket === 'LGA1151') return 'DDR4';

    if (/\b(X870E|X870|B850|X670E|X670|B650E|B650|A620|Z890|B860|H810)\b/i.test(text)) return 'DDR5';
    if (/\b(X570|B550|A520|X470|B450|A320|H510|B560|Z590|H410|B460|Z490)\b/i.test(text)) return 'DDR4';

    if (socket === 'LGA1700' || /\b(Z790|B760|H770|H610|Z690|B660)\b/i.test(text)) {
      if (/\bD4\b|DDR4/i.test(text)) return 'DDR4';
      if (/\bD5\b|DDR5/i.test(text)) return 'DDR5';
      return 'DDR5';
    }

    return null;
  }

  function inferFormFactor(it) {
    if (!it) return null;
    const raw = norm(it.s?.form_factor || it.s?.case_max || '');
    if (/MINI-?ITX|ITX/i.test(raw)) return 'Mini-ITX';
    if (/MICRO-?ATX|M-?ATX|MATX/i.test(raw)) return 'Micro-ATX';
    if (/E-?ATX/i.test(raw)) return 'E-ATX';
    if (/ATX/i.test(raw)) return 'ATX';

    const text = upper(`${it.t || ''} ${(it.tags || []).join(' ')}`);
    if (/MINI-?ITX|\bITX\b/i.test(text)) return 'Mini-ITX';
    if (/MICRO-?ATX|M-?ATX|\bMATX\b|M-ATX/i.test(text)) return 'Micro-ATX';
    if (/E-?ATX/i.test(text)) return 'E-ATX';
    if (/\bATX\b/i.test(text)) return 'ATX';
    return null;
  }

  const num = v => {
    if (v == null) return null;
    const m = String(v).match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  };

  function estimateCpuTdp(cpu) {
    if (!cpu) return 0;
    const direct = num(cpu.s?.tdp);
    if (direct && direct > 20) return direct;
    const text = upper(`${cpu.t || ''} ${cpu.s?.processor || ''}`);
    if (/I9|RYZEN 9|ULTRA 9|9950|9900|7950|7900|14900|13900|285K/i.test(text)) return 220;
    if (/I7|RYZEN 7|ULTRA 7|9800X3D|7800X3D|9700|7700|14700|13700|265K/i.test(text)) return 150;
    if (/I5|RYZEN 5|ULTRA 5|9600|7600|14600|14400|13600|13400|12400|245K/i.test(text)) return 100;
    if (/I3|RYZEN 3|14100|12100/i.test(text)) return 65;
    return 95;
  }

  function estimateGpuTdp(gpu) {
    if (!gpu) return 0;
    const direct = num(gpu.s?.tdp);
    if (direct && direct > 30) return direct;
    const text = upper(`${gpu.t || ''} ${gpu.s?.gpu_chipset || ''}`);
    if (/RTX 5090|RTX 4090/i.test(text)) return 450;
    if (/RTX 5080|RTX 4080/i.test(text)) return 320;
    if (/RTX 5070 TI|RTX 4070 TI|RTX 4070 SUPER|RX 7900/i.test(text)) return 285;
    if (/RTX 5070|RTX 4070|RX 7800/i.test(text)) return 220;
    if (/RTX 4060 TI|RTX 3070|RX 7700/i.test(text)) return 180;
    if (/RTX 4060|RTX 3060|RX 7600|RTX 3050/i.test(text)) return 130;
    if (/GTX 16|GTX 10|GT 1030/i.test(text)) return 75;
    return 180;
  }

  function estimatePower(build) {
    const cpuT = estimateCpuTdp(build.cpu);
    const gpuT = estimateGpuTdp(build.gpu);
    const ramCount = (build.ram || []).filter(Boolean).length;
    const driveCount = (build.storage || []).filter(Boolean).length;
    const coolerExtra = build.cooler ? (/AIO|LIQUID|360|240/i.test(build.cooler.t || '') ? 25 : 10) : 10;
    const base = 60;

    if (!cpuT && !gpuT && !ramCount && !driveCount) return null;

    const draw = cpuT + gpuT + (ramCount * 10) + (driveCount * 8) + coolerExtra + base;
    const recommended = Math.ceil((draw * 1.35) / 50) * 50;

    return {
      draw: Math.round(draw),
      recommended: Math.max(500, recommended),
      cpuWatts: cpuT,
      gpuWatts: gpuT,
    };
  }

  function checkSocket(cpu, mobo) {
    if (!cpu || !mobo) return null;
    const sockCpu = inferSocket(cpu);
    const sockMobo = inferSocket(mobo);

    if (!sockCpu || !sockMobo) return null;
    if (sockCpu === sockMobo) return { ok: true, socket: sockCpu };

    return {
      ok: false,
      rule: 'socket',
      message: `${shortName(cpu)} uses ${sockCpu} socket, but ${shortName(mobo)} is ${sockMobo}.`,
      short: `Requires ${sockMobo}`,
    };
  }

  function checkMemoryGen(mobo, ramList) {
    const moboGen = inferRamType(mobo);
    const sticks = (ramList || []).filter(Boolean);
    if (!sticks.length) return null;

    const ramGens = [...new Set(sticks.map(inferRamType).filter(Boolean))];
    if (ramGens.length > 1) {
      return {
        ok: false,
        rule: 'memory-mixed',
        message: `Your build mixes ${ramGens.join(' and ')} memory. A PC can only use one generation.`,
        short: 'Mixed DDR generations',
      };
    }

    if (!moboGen) return null;
    const badStick = sticks.find(r => {
      const rGen = inferRamType(r);
      return rGen && rGen !== moboGen;
    });

    if (badStick) {
      const stickGen = inferRamType(badStick);
      return {
        ok: false,
        rule: 'memory-gen',
        message: `${shortName(mobo)} requires ${moboGen} memory, but ${shortName(badStick)} is ${stickGen}.`,
        short: `Requires ${moboGen}`,
      };
    }

    return { ok: true, ramType: moboGen };
  }

  function checkFormFactor(mobo, pcase) {
    if (!mobo || !pcase) return null;
    const ffMobo = inferFormFactor(mobo);
    const ffCase = inferFormFactor(pcase);
    if (!ffMobo || !ffCase) return null;

    const ranks = { 'Mini-ITX': 1, 'Micro-ATX': 2, 'ATX': 3, 'E-ATX': 4 };
    const moboRank = ranks[ffMobo];
    const caseRank = ranks[ffCase];

    if (moboRank && caseRank && moboRank > caseRank) {
      return {
        ok: false,
        rule: 'form-factor',
        message: `${shortName(mobo)} is ${ffMobo}, but ${shortName(pcase)} only supports up to ${ffCase}.`,
        short: `Requires ${ffMobo} Case`,
      };
    }
    return { ok: true, moboFF: ffMobo, caseFF: ffCase };
  }

  function checkPsuHeadroom(build, headroomPct = 35) {
    if (!build.psu) return null;
    const psuW = num(build.psu.s?.psu_w) || num(build.psu.t);
    const est = estimatePower(build);
    if (!psuW || !est) return null;

    const recommended = Math.ceil((est.draw * (1 + headroomPct / 100)) / 50) * 50;

    // Hard incompatibility: PSU cannot supply the base hardware draw
    if (psuW < est.draw) {
      return {
        ok: false,
        isWarning: false,
        rule: 'psu-under',
        message: `Estimated draw is ~${est.draw}W, but ${shortName(build.psu)} is only ${psuW}W. A higher-capacity PSU is required.`,
        short: 'PSU wattage too low',
      };
    }

    // Soft advisory warning: Above base draw, but less than recommended 35% headroom
    if (psuW < recommended) {
      return {
        ok: true,
        isWarning: true,
        rule: 'psu-tight',
        message: `System draws ~${est.draw}W with a ${psuW}W PSU. Headroom is tight; ${recommended}W+ is recommended for peak transient spikes.`,
        short: 'PSU headroom tight',
      };
    }

    return { ok: true, isWarning: false, psuWatts: psuW, recommendedWatts: recommended };
  }

  const CORE_KEYS = ['cpu', 'motherboard', 'ram', 'storage', 'psu', 'case'];

  function collectItems(build) {
    const out = [];
    for (const [slot, v] of Object.entries(build)) {
      if (!v) continue;
      if (Array.isArray(v)) v.filter(Boolean).forEach(i => out.push({ ...i, slot }));
      else out.push({ ...v, slot });
    }
    return out;
  }

  function evaluate(build, opts = {}) {
    const blocks = [];
    const warnings = [];
    const push = r => {
      if (!r) return;
      if (r.ok === false) blocks.push(r);
      else if (r.isWarning) warnings.push(r);
    };

    const sock = checkSocket(build.cpu, build.motherboard);
    const mem = checkMemoryGen(build.motherboard, build.ram);
    const ff = checkFormFactor(build.motherboard, build.case);
    const psu = checkPsuHeadroom(build, opts.headroomPct);

    push(sock);
    push(mem);
    push(ff);
    push(psu);

    const items = collectItems(build);
    const missing = CORE_KEYS.filter(k => {
      const v = build[k];
      return Array.isArray(v) ? !v.filter(Boolean).length : !v;
    });

    const hasSoldOut = items.some(it => it.avail === false);
    const power = estimatePower(build);

    return {
      blocks,
      warnings,
      sock,
      mem,
      ff,
      psu,
      missing,
      hasSoldOut,
      complete: missing.length === 0,
      coreCount: CORE_KEYS.length - missing.length,
      coreTotal: CORE_KEYS.length,
      power,
      totalCents: items.reduce((sum, it) => sum + (it.cents || 0) * (it.qty || 1), 0),
      itemCount: items.reduce((sum, it) => sum + (it.qty || 1), 0),
      status: blocks.length ? 'block' : items.length ? 'ok' : 'empty',
    };
  }

  function candidateCheck(slot, item, build) {
    const trial = { ...build };
    if (slot === 'ram' || slot === 'storage') {
      trial[slot] = [...(build[slot] || []).filter(Boolean), item];
    } else {
      trial[slot] = item;
    }

    // Strict Case Eligibility: Reject any liquid cooler or cooling gear in Case slot
    if (slot === 'case') {
      const t = (item.t || '').toLowerCase();
      const type = (item.productType || '').toLowerCase();
      if (type === 'cooling' || /\b(liquid cooler|cpu cooler|\baio\b|radiator)\b/i.test(t)) {
        return { ok: false, reason: 'Coolers cannot be selected as PC Case' };
      }
    }

    if (slot === 'cpu' || slot === 'motherboard') {
      const s = checkSocket(trial.cpu, trial.motherboard);
      if (s && !s.ok) return { ok: false, reason: s.short || 'Incompatible socket' };
    }

    if (slot === 'ram' || slot === 'motherboard') {
      const m = checkMemoryGen(trial.motherboard, trial.ram);
      if (m && !m.ok) return { ok: false, reason: m.short || 'Incompatible RAM generation' };
    }

    if (slot === 'case' || slot === 'motherboard') {
      const f = checkFormFactor(trial.motherboard, trial.case);
      if (f && !f.ok) return { ok: false, reason: f.short || 'Case cannot fit motherboard size' };
    }

    // Filter undersized PSUs before selection when checking compatibility
    if (slot === 'psu') {
      const psuW = num(item.s?.psu_w) || num(item.t);
      const est = estimatePower(build);
      if (psuW && est && est.draw > 0 && psuW < est.draw) {
        return { ok: false, reason: `Rated ${psuW}W is below estimated system draw (~${est.draw}W)` };
      }
    }

    return { ok: true };
  }

  /* ==========================================================================
     2. UI CONTROLLER & CART API
     ========================================================================== */

  const SLOT_ICONS = {
    cpu: '⚙️',
    motherboard: '🔲',
    ram: '💾',
    gpu: '🎮',
    storage: '💽',
    psu: '⚡',
    case: '🖥️',
    cooler: '❄️',
    monitor: '🖥️'
  };

  function formatMoney(cents) {
    if (typeof Number.prototype.toCurrency === 'function') {
      return Number(cents).toCurrency();
    }
    const jod = (cents / 100).toFixed(2);
    return `${jod} JOD`;
  }

  function esc(str) {
    if (!str) return '';
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(str).replace(/[&<>"']/g, m => map[m]);
  }

  function initPCBuilder(root) {
    if (!root || root.dataset.pcbMounted === 'true') return;
    root.dataset.pcbMounted = 'true';

    const configEl = root.querySelector('[data-pcb-config]');
    if (!configEl) return;
    let cfg;
    try {
      cfg = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('[PCB] Config parse error:', e);
      return;
    }

    const state = {
      cfg,
      slots: (cfg.slots || []).filter(s => s.handle),
      pool: {},
      picked: {},
      buildId: null,
      openSlot: null,
      searchQuery: '',
      compatOnly: true,
      inStockOnly: true, // Default to in-stock only
    };

    let lastFocusedTrigger = null;

    cfg.slots.forEach(s => {
      state.picked[s.key] = s.max > 1 ? [] : null;
    });

    const $ = sel => root.querySelector(sel);
    const $$ = sel => [...root.querySelectorAll(sel)];

    async function loadSlotProducts(handle) {
      if (state.pool[handle] && state.pool[handle].length > 0) {
        return state.pool[handle];
      }

      try {
        const url = `/collections/${handle}?view=pcb`;
        const res = await fetch(url).then(r => r.json());
        let items = res.items || [];

        if (res.pages > 1) {
          const extraPages = await Promise.all(
            Array.from({ length: res.pages - 1 }, (_, i) =>
              fetch(`/collections/${handle}?view=pcb&page=${i + 2}`).then(r => r.json()).catch(() => ({ items: [] }))
            )
          );
          extraPages.forEach(p => {
            if (p.items) items = items.concat(p.items);
          });
        }

        // Defensive filter for Case slot
        if (handle === 'pcb-case') {
          items = items.filter(it => {
            const t = (it.t || '').toLowerCase();
            return !(/\b(liquid cooler|cpu cooler|\baio\b|radiator)\b/i.test(t) || (it.s?.category || '').toLowerCase().includes('cooler'));
          });
        }

        state.pool[handle] = items;
        return items;
      } catch (e) {
        console.error(`[PCB] Failed loading ${handle}:`, e);
        return [];
      }
    }

    function prefetchSlots() {
      state.slots.forEach(s => loadSlotProducts(s.handle).catch(() => {}));
    }

    function ruleBuild() {
      const b = {};
      for (const s of state.slots) {
        const v = state.picked[s.key];
        if (s.key === 'ram' || s.key === 'storage') {
          b[s.key] = Array.isArray(v) ? v.filter(Boolean) : [];
        } else {
          b[s.key] = v || null;
        }
      }
      return b;
    }

    function getPickedList(slotKey) {
      const v = state.picked[slotKey];
      return Array.isArray(v) ? v.filter(Boolean) : v ? [v] : [];
    }

    function setPick(slotKey, item) {
      const slot = state.slots.find(s => s.key === slotKey);
      if (!slot) return;

      if (slot.max > 1) {
        const arr = state.picked[slotKey] || [];
        if (arr.length >= slot.max) arr.shift();
        state.picked[slotKey] = [...arr, item];
      } else {
        state.picked[slotKey] = item;
      }

      if (!state.buildId) state.buildId = 'IGEEK-' + Math.random().toString(36).slice(2, 7).toUpperCase();
      renderApp();
      showToast(`Added ${shortName(item)}`);
    }

    function removePick(slotKey, index = 0) {
      if (Array.isArray(state.picked[slotKey])) {
        state.picked[slotKey].splice(index, 1);
      } else {
        state.picked[slotKey] = null;
      }
      renderApp();
    }

    function getSpecPills(key, item) {
      const pills = [];
      const ram = inferRamType(item);
      const sock = inferSocket(item);
      const ff = inferFormFactor(item);

      if (key === 'cpu') {
        if (sock) pills.push(`Socket ${sock}`);
        if (item.s?.processor) pills.push(item.s.processor);
      } else if (key === 'motherboard') {
        if (sock) pills.push(`Socket ${sock}`);
        if (ram) pills.push(ram);
        if (ff) pills.push(ff);
        if (item.s?.chipset) pills.push(item.s.chipset);
      } else if (key === 'ram') {
        if (ram) pills.push(ram);
        if (item.s?.capacity) pills.push(item.s.capacity);
      } else if (key === 'gpu') {
        if (item.s?.gpu_chipset) pills.push(item.s.gpu_chipset);
        if (item.s?.vram) pills.push(item.s.vram);
      } else if (key === 'storage') {
        if (item.s?.storage_if) pills.push(item.s.storage_if);
        if (item.s?.capacity) pills.push(item.s.capacity);
      } else if (key === 'psu') {
        if (item.s?.psu_w) pills.push(`${item.s.psu_w}W`);
        if (item.s?.psu_rating) pills.push(item.s.psu_rating);
      } else if (key === 'case') {
        if (item.s?.case_max || ff) pills.push(`Fits ${item.s?.case_max || ff}`);
      }

      if (!pills.length && item.vendor) pills.push(item.vendor);
      return pills;
    }

    function renderSlotCard(slot) {
      const list = getPickedList(slot.key);
      const isFilled = list.length > 0;
      const canAddMore = list.length < slot.max;
      const icon = SLOT_ICONS[slot.key] || '⚡';

      const card = document.createElement('div');
      card.className = `pcb-slot-card${isFilled ? ' is-filled' : ''}${slot.extra ? ' is-optional' : ''}`;
      card.dataset.slot = slot.key;

      let countBadge = '';
      if (slot.max > 1) {
        countBadge = `<span class="pcb-slot-count-badge">${list.length}/${slot.max} Selected</span>`;
      }

      const headerHtml = `
        <div class="pcb-slot-card-header">
          <div class="pcb-slot-badge-wrap">
            <div class="pcb-slot-icon-box">${icon}</div>
            <span class="pcb-slot-name">${esc(slot.label)}</span>
            <span class="pcb-slot-tag ${slot.required ? 'is-req' : 'is-opt'}">
              ${slot.required ? 'Core' : 'Optional'}
            </span>
          </div>
          ${countBadge}
        </div>
      `;

      const itemsHtml = list.map((item, i) => {
        const pills = getSpecPills(slot.key, item);
        const pillsHtml = pills.map(p => `<span class="pcb-spec-pill">${esc(p)}</span>`).join('');
        const thumb = item.img ? `<img class="pcb-item-thumb" src="${item.img}" alt="${esc(item.t)}" loading="lazy">` : '';
        const isSoldOut = item.avail === false;
        const stockBadge = isSoldOut ? '<span class="pcb-spec-pill is-sold-out" style="color: #ef4444; border-color: #fecaca; margin-top: 4px;">Out of Stock</span>' : '';

        return `
          <div class="pcb-picked-item${isSoldOut ? ' is-sold-out' : ''}">
            ${thumb}
            <div class="pcb-item-details">
              <div class="pcb-item-brand">${esc(item.vendor || item.s?.brand || '')}</div>
              <div class="pcb-item-title" title="${esc(item.t)}">${esc(item.t)}</div>
              <div class="pcb-item-spec-pills">${pillsHtml} ${stockBadge}</div>
            </div>
            <div class="pcb-item-pricing">
              <div class="pcb-item-price">${item.money || formatMoney(item.cents)}</div>
              <div class="pcb-item-actions">
                ${slot.max === 1 ? `<button class="pcb-action-link" data-change="${slot.key}">Change</button>` : ''}
                <button class="pcb-action-link is-remove" data-remove="${slot.key}:${i}">Remove</button>
              </div>
            </div>
          </div>
        `;
      }).join('');

      let emptyHtml = '';
      if (canAddMore) {
        const btnText = list.length > 0 ? `+ Add Another ${esc(slot.label)}` : `+ Choose ${esc(slot.label)}`;
        emptyHtml = `
          <div class="pcb-empty-slot">
            <button class="pcb-select-btn" data-choose="${slot.key}">
              <span>${btnText}</span>
            </button>
          </div>
        `;
      }

      card.innerHTML = headerHtml + itemsHtml + emptyHtml;
      return card;
    }

    function renderApp() {
      const slotsHost = $('[data-pcb-slots]');
      if (!slotsHost) return;
      slotsHost.innerHTML = '';

      const coreSlots = state.slots.filter(s => !s.extra);
      const extraSlots = state.slots.filter(s => s.extra);

      coreSlots.forEach(s => slotsHost.appendChild(renderSlotCard(s)));

      if (extraSlots.length) {
        const div = document.createElement('div');
        div.className = 'pcb-extras-divider';
        div.innerHTML = `
          <span class="pcb-extras-title">Peripherals & Add-ons</span>
          <div class="pcb-extras-line"></div>
        `;
        slotsHost.appendChild(div);
        extraSlots.forEach(s => slotsHost.appendChild(renderSlotCard(s)));
      }

      const res = evaluate(ruleBuild(), { headroomPct: state.cfg.headroomPct });
      renderProgress(res);
      renderStatusBox(res);
      renderPowerGauge(res);
      renderTotals(res);
    }

    function renderProgress(res) {
      const pct = Math.round((res.coreCount / res.coreTotal) * 100);
      const fill = $('[data-pcb-progress-fill]');
      const count = $('[data-pcb-progress-count]');
      const pctEl = $('[data-pcb-progress-pct]');

      if (fill) fill.style.width = `${pct}%`;
      if (count) count.textContent = `${res.coreCount} of ${res.coreTotal} Core Parts Selected`;
      if (pctEl) pctEl.textContent = `${pct}%`;
    }

    function renderStatusBox(res) {
      const host = $('[data-pcb-status]');
      if (!host) return;

      if (!res.itemCount) {
        host.innerHTML = `
          <div class="pcb-status-banner is-empty">
            <span style="font-size: 20px;">⚡</span>
            <div>
              <strong>Configure Your PC</strong>
              <span>Pick a Processor or Motherboard to start checking hardware compatibility.</span>
            </div>
          </div>
        `;
        return;
      }

      let banners = '';

      // Hard incompatibilities
      if (res.blocks.length > 0) {
        for (const b of res.blocks) {
          banners += `
            <div class="pcb-status-banner is-block">
              <div>
                <strong>Incompatible Selection</strong>
                <span>${esc(b.message)}</span>
              </div>
            </div>
          `;
        }
      }

      // Advisory Warnings (e.g. PSU headroom)
      if (res.warnings && res.warnings.length > 0) {
        for (const w of res.warnings) {
          banners += `
            <div class="pcb-status-banner is-warn">
              <div>
                <strong>PSU Capacity Advisory</strong>
                <span>${esc(w.message)}</span>
              </div>
            </div>
          `;
        }
      }

      // Out of stock warning in selected parts
      if (res.hasSoldOut) {
        banners += `
          <div class="pcb-status-banner is-warn">
            <div>
              <strong>Availability Warning</strong>
              <span>One or more selected parts are currently out of stock. Please replace them to complete your order.</span>
            </div>
          </div>
        `;
      }

      // Compatibility status
      if (!res.blocks.length) {
        if (res.complete) {
          banners += `
            <div class="pcb-status-banner is-ok">
              <div>
                <strong>Core Hardware Compatible</strong>
                <span>Socket, RAM generation, case form factor & wattage verified. Physical clearances (GPU length, cooler clearance & BIOS) will be reviewed by iGeek technicians prior to assembly.</span>
              </div>
            </div>
          `;
        } else {
          banners += `
            <div class="pcb-status-banner is-ok">
              <div>
                <strong>Compatible So Far</strong>
                <span>Missing core parts: ${res.missing.map(m => m.toUpperCase()).join(', ')}</span>
              </div>
            </div>
          `;
        }
      }

      host.innerHTML = banners;
    }

    function renderPowerGauge(res) {
      const card = $('[data-pcb-power]');
      if (!card) return;

      if (res.power && res.power.draw > 0) {
        card.hidden = false;
        $('[data-pcb-power-draw]').textContent = `${res.power.draw} W`;
        $('[data-pcb-power-rec]').textContent = `${res.power.recommended} W+`;
        const fillPct = Math.min(100, Math.round((res.power.draw / res.power.recommended) * 100));
        $('[data-pcb-power-fill]').style.width = `${fillPct}%`;
      } else {
        card.hidden = true;
      }
    }

    function renderTotals(res) {
      const totalFormatted = res.totalCents ? formatMoney(res.totalCents) : '0.00 JOD';
      const totalEl = $('[data-pcb-total]');
      if (totalEl) totalEl.textContent = totalFormatted;

      const mobTotal = $('[data-pcb-bar-total]');
      if (mobTotal) mobTotal.textContent = totalFormatted;

      const mobCount = $('[data-pcb-bar-count]');
      if (mobCount) mobCount.textContent = `${res.itemCount} Part${res.itemCount === 1 ? '' : 's'}`;

      const mobDock = $('[data-pcb-bar]');
      if (mobDock) mobDock.hidden = res.itemCount === 0;

      const hasBlocks = res.blocks.length > 0;
      const isComplete = res.complete;
      const hasSoldOut = res.hasSoldOut;
      const canAdd = isComplete && !hasBlocks && !hasSoldOut;

      const addBtns = $$('[data-pcb-add]');
      addBtns.forEach(b => {
        b.disabled = !canAdd;
        const textSpan = b.querySelector('span');
        if (textSpan) {
          if (hasBlocks) {
            textSpan.textContent = 'Incompatible Build Selection';
          } else if (!isComplete) {
            textSpan.textContent = `Select All Core Parts (${res.coreCount}/${res.coreTotal})`;
          } else if (hasSoldOut) {
            textSpan.textContent = 'Selected Part(s) Out of Stock';
          } else {
            textSpan.textContent = state.cfg.add_label || 'Add Full Build to Cart';
          }
        }
      });

      const mobAddBtns = $$('[data-pcb-add-mobile]');
      mobAddBtns.forEach(b => {
        b.disabled = !canAdd;
        const textSpan = b.querySelector('span');
        if (textSpan) {
          if (hasBlocks) {
            textSpan.textContent = 'Incompatible';
          } else if (!isComplete) {
            textSpan.textContent = `${res.coreCount}/${res.coreTotal} Core Parts`;
          } else if (hasSoldOut) {
            textSpan.textContent = 'Sold Out Item';
          } else {
            textSpan.textContent = 'Add to Cart';
          }
        }
      });
    }

    /* ------------------------------------------------------------ Part Picker Drawer */

    async function openDrawer(slotKey) {
      lastFocusedTrigger = document.activeElement;
      const slot = state.slots.find(s => s.key === slotKey);
      if (!slot) return;
      state.openSlot = slot;

      $('[data-pcb-sheet-title]').textContent = `Choose ${slot.label}`;
      $('[data-pcb-sheet-subtitle]').textContent = `Showing verified items for ${slot.label}`;
      $('[data-pcb-sheet-icon]').textContent = SLOT_ICONS[slot.key] || '⚡';
      $('[data-pcb-search]').value = '';
      state.searchQuery = '';

      const sheet = $('[data-pcb-sheet]');
      const scrim = $('[data-pcb-scrim]');
      if (sheet) { sheet.hidden = false; sheet.classList.add('is-open'); }
      if (scrim) { scrim.hidden = false; scrim.classList.add('is-open'); }
      document.body.style.overflow = 'hidden';

      // Move focus into the drawer's search input
      setTimeout(() => {
        const searchEl = $('[data-pcb-search]');
        if (searchEl) searchEl.focus();
        else {
          const closeEl = $('[data-pcb-sheet-close]');
          if (closeEl) closeEl.focus();
        }
      }, 60);

      if (state.pool[slot.handle] && state.pool[slot.handle].length > 0) {
        renderDrawerItems();
      } else {
        $('[data-pcb-sheet-list]').innerHTML = `
          <div class="pcb-drawer-loading">
            <div class="pcb-spinner"></div>
            <span>Loading catalog items...</span>
          </div>
        `;
        await loadSlotProducts(slot.handle);
        renderDrawerItems();
      }
    }

    function closeDrawer() {
      const sheet = $('[data-pcb-sheet]');
      const scrim = $('[data-pcb-scrim]');
      if (sheet) { sheet.hidden = true; sheet.classList.remove('is-open'); }
      if (scrim) { scrim.hidden = true; scrim.classList.remove('is-open'); }
      document.body.style.overflow = '';
      state.openSlot = null;

      // Return focus to triggering button
      if (lastFocusedTrigger && typeof lastFocusedTrigger.focus === 'function') {
        try { lastFocusedTrigger.focus(); } catch (e) {}
      }
    }

    function renderDrawerItems() {
      const slot = state.openSlot;
      if (!slot) return;

      try {
        const allItems = state.pool[slot.handle] || [];
        const build = ruleBuild();
        const q = (state.searchQuery || '').trim().toLowerCase();
        const compatOnly = state.compatOnly;
        const inStockOnly = state.inStockOnly;

        const scored = allItems.map(item => ({
          item,
          verdict: candidateCheck(slot.key, item, build)
        }));

        const filtered = scored.filter(({ item, verdict }) => {
          if (inStockOnly && !item.avail) return false;
          if (compatOnly && !verdict.ok) return false;
          if (q) {
            const text = `${item.t || ''} ${item.vendor || ''}`.toLowerCase();
            if (!text.includes(q)) return false;
          }
          return true;
        });

        filtered.sort((a, b) => (b.item.avail - a.item.avail) || (a.item.cents - b.item.cents));

        const listHost = $('[data-pcb-sheet-list]');
        if (!listHost) return;

        if (!filtered.length) {
          listHost.innerHTML = `
            <div class="pcb-drawer-loading">
              <span style="font-size: 28px;">🔍</span>
              <strong style="color: var(--pcb-slate-800);">No matching parts found</strong>
              <button class="pcb-btn-secondary" style="margin-top: 10px;" data-pcb-reset-filter>Show All Parts</button>
            </div>
          `;
          return;
        }

        listHost.innerHTML = filtered.map(({ item, verdict }) => {
          const pills = getSpecPills(slot.key, item);
          const pillsHtml = pills.map(p => `<span class="pcb-spec-pill">${esc(p)}</span>`).join('');
          const thumb = item.img ? `<img class="pcb-candidate-thumb" src="${item.img}" alt="${esc(item.t)}" loading="lazy">` : '';
          const isCompat = verdict.ok;
          const isAvailable = item.avail !== false;
          const canSelect = isCompat && isAvailable;

          let btnText = 'Select';
          if (!isCompat) {
            btnText = 'Incompatible';
          } else if (!isAvailable) {
            btnText = 'Sold Out';
          }

          const stockPill = isAvailable
            ? ''
            : '<span class="pcb-spec-pill is-sold-out" style="color: #ef4444; border-color: #fecaca;">Out of Stock</span>';

          return `
            <div class="pcb-candidate-row${isCompat ? '' : ' is-incompatible'}${isAvailable ? '' : ' is-sold-out'}">
              ${thumb}
              <div class="pcb-candidate-info">
                <div class="pcb-candidate-title">${esc(item.t)}</div>
                <div class="pcb-candidate-specs">
                  ${pillsHtml}
                  ${stockPill}
                </div>
                ${isCompat ? '' : `<div class="pcb-candidate-reason">✕ ${esc(verdict.reason)}</div>`}
              </div>
              <div class="pcb-candidate-cta">
                <div class="pcb-candidate-price">${item.money || formatMoney(item.cents)}</div>
                <button class="pcb-choose-btn" data-pick="${item.vid}" ${canSelect ? '' : 'disabled'}>
                  ${btnText}
                </button>
              </div>
            </div>
          `;
        }).join('');
      } catch (err) {
        console.error('[PCB] renderDrawerItems error:', err);
      }
    }

    async function handleAddToCart() {
      const build = ruleBuild();
      const res = evaluate(build, { headroomPct: state.cfg.headroomPct });
      const items = collectItems(build);

      if (!res.complete || res.blocks.length > 0 || res.hasSoldOut) {
        if (!res.complete) showToast(`Please select all core components (${res.coreCount}/${res.coreTotal})`);
        else if (res.hasSoldOut) showToast('Some selected parts are out of stock. Please replace them.');
        else showToast('Please fix incompatible component selections.');
        return;
      }

      if (!items.length) return;

      const btns = $$('[data-pcb-add], [data-pcb-add-mobile]');
      btns.forEach(b => {
        b.disabled = true;
        b.innerHTML = '<span>Adding to Cart...</span>';
      });

      const lines = new Map();
      for (const it of items) {
        if (lines.has(it.vid)) {
          lines.get(it.vid).quantity += 1;
        } else {
          lines.set(it.vid, {
            id: it.vid,
            quantity: 1,
            properties: {
              'Custom PC Build': state.buildId,
              'Slot': it.slot.toUpperCase()
            }
          });
        }
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [...lines.values()] })
        });

        if (res.ok) {
          window.location.href = '/cart';
        } else {
          const data = await res.json();
          alert(data.description || 'Could not add to cart.');
          renderApp();
        }
      } catch (e) {
        console.error('[PCB] Cart error:', e);
        renderApp();
      }
    }

    function showToast(msg) {
      const toast = $('[data-pcb-toast]');
      if (!toast) return;
      toast.textContent = msg;
      toast.classList.add('is-visible');
      setTimeout(() => toast.classList.remove('is-visible'), 3000);
    }

    /* ------------------------------------------------------------ Share Build Engine */

    function encodeBuild() {
      const compact = {};
      for (const [k, v] of Object.entries(state.picked)) {
        if (!v) continue;
        if (Array.isArray(v)) {
          const vids = v.filter(Boolean).map(i => i.vid);
          if (vids.length) compact[k] = vids;
        } else if (v.vid) {
          compact[k] = v.vid;
        }
      }
      return encodeURIComponent(btoa(JSON.stringify(compact)));
    }

    async function handleShareBuild() {
      const items = collectItems(ruleBuild());
      if (!items.length) {
        showToast('Please select components before sharing your build.');
        return;
      }

      const encoded = encodeBuild();
      const url = new URL(window.location.href);
      url.searchParams.set('build', encoded);
      const shareUrl = url.toString();
      window.history.replaceState(null, '', shareUrl);

      const res = evaluate(ruleBuild());
      const shareText = `Check out my custom PC build (${formatMoney(res.totalCents)}) on iGeek Megastore!`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: 'My Custom PC Build | iGeek Megastore',
            text: shareText,
            url: shareUrl
          });
          return;
        } catch (err) {
          // User cancelled share dialog or declined permission
        }
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          showToast('✓ Build link copied to clipboard!');
          return;
        } catch (e) {}
      }

      window.prompt('Copy your custom build link:', shareUrl);
    }

    async function restoreBuildFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const buildParam = params.get('build');
      if (!buildParam) return;

      try {
        const jsonStr = atob(decodeURIComponent(buildParam));
        const compact = JSON.parse(jsonStr);
        if (!compact || typeof compact !== 'object') return;

        // Prefetch all slot catalogs so items are present in state.pool
        await Promise.all(state.slots.map(s => loadSlotProducts(s.handle)));

        let restoredCount = 0;
        for (const [slotKey, val] of Object.entries(compact)) {
          const slot = state.slots.find(s => s.key === slotKey);
          if (!slot) continue;
          const pool = state.pool[slot.handle] || [];

          if (Array.isArray(val)) {
            state.picked[slotKey] = val.map(vid => pool.find(i => String(i.vid) === String(vid))).filter(Boolean);
            restoredCount += state.picked[slotKey].length;
          } else {
            const item = pool.find(i => String(i.vid) === String(val));
            if (item) {
              state.picked[slotKey] = item;
              restoredCount++;
            }
          }
        }

        if (restoredCount > 0) {
          renderApp();
          showToast(`✓ Restored ${restoredCount} components from shared build!`);
        }
      } catch (e) {
        console.warn('[PCB] Could not parse build from URL:', e);
      }
    }

    /* ------------------------------------------------------------ PDF / Print Quotation Sheet */

    function printBuildSpecSheet() {
      const build = ruleBuild();
      const items = collectItems(build);
      const res = evaluate(build, { headroomPct: state.cfg.headroomPct });
      const totalFormatted = res.totalCents ? formatMoney(res.totalCents) : '0.00 JOD';
      const buildId = state.buildId || 'IGEEK-' + Math.random().toString(36).slice(2, 7).toUpperCase();

      const d = new Date();
      const dateStr = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
      const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      let rowsHtml = '';
      if (!items.length) {
        rowsHtml = `
          <tr>
            <td colspan="4" style="text-align: center; padding: 24px; color: #64748b; font-style: italic;">
              No hardware components selected yet.
            </td>
          </tr>
        `;
      } else {
        rowsHtml = items.map((it, idx) => {
          const slot = state.slots.find(s => s.key === it.slot);
          const pills = getSpecPills(it.slot, it).join(' · ');
          const unitPrice = it.money || formatMoney(it.cents);
          const bg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';

          return `
            <tr style="background: ${bg}; border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 9px 12px; font-weight: 700; color: #0090d0; text-transform: uppercase; font-size: 11px; vertical-align: top;">
                ${esc(slot?.label || it.slot)}
              </td>
              <td style="padding: 9px 12px; vertical-align: top;">
                <div style="font-weight: 600; color: #0f172a; font-size: 12.5px; line-height: 1.35; margin-bottom: 2px;">
                  ${esc(it.t)}
                </div>
                ${pills ? `<div style="font-size: 10.5px; color: #64748b;">${esc(pills)}</div>` : ''}
              </td>
              <td style="padding: 9px 12px; text-align: center; font-weight: 600; vertical-align: top;">1</td>
              <td style="padding: 9px 12px; text-align: right; font-weight: 700; font-family: monospace; font-size: 12px; color: #0f172a; vertical-align: top;">
                ${unitPrice}
              </td>
            </tr>
          `;
        }).join('');
      }

      const drawText = res.power && res.power.draw > 0 ? `${res.power.draw} W` : '60 W (Baseline)';
      const psuText = res.power && res.power.recommended > 0 ? `${res.power.recommended} W+` : '550 W+';

      // Real Compatibility Verdict
      let compatStatusHtml = '';
      if (!items.length) {
        compatStatusHtml = `<span style="color: #64748b; font-weight: 700;">No Components Selected</span>`;
      } else if (res.blocks.length > 0) {
        const errorList = res.blocks.map(b => esc(b.short || b.message)).join(', ');
        compatStatusHtml = `<span style="color: #ef4444; font-weight: 700;">✕ Incompatible (${errorList})</span>`;
      } else if (!res.complete) {
        const missingList = res.missing.map(m => m.toUpperCase()).join(', ');
        compatStatusHtml = `<span style="color: #f59e0b; font-weight: 700;">⚠️ Incomplete (${res.coreCount}/${res.coreTotal} Core Parts — Missing: ${missingList})</span>`;
      } else {
        compatStatusHtml = `<span style="color: #10b981; font-weight: 700;">✓ Core Hardware Verified</span>`;
      }

      // Real Technical Diagnostics Details
      const sockDiag = res.sock
        ? (res.sock.ok
          ? `<span style="color: #10b981; font-weight: 600;">✓ ${res.sock.socket} Match</span>`
          : `<span style="color: #ef4444; font-weight: 600;">✕ ${esc(res.sock.short)}</span>`)
        : (build.cpu || build.motherboard
          ? `<span style="color: #64748b;">${inferSocket(build.cpu) || inferSocket(build.motherboard) || 'Socket Selected'}</span>`
          : `<span style="color: #94a3b8;">Pending CPU & Motherboard</span>`);

      const memDiag = res.mem
        ? (res.mem.ok
          ? `<span style="color: #10b981; font-weight: 600;">✓ ${res.mem.ramType} Match</span>`
          : `<span style="color: #ef4444; font-weight: 600;">✕ ${esc(res.mem.short)}</span>`)
        : (build.motherboard || (build.ram && build.ram.length)
          ? `<span style="color: #64748b;">${inferRamType(build.motherboard) || 'RAM Configured'}</span>`
          : `<span style="color: #94a3b8;">Pending Motherboard & RAM</span>`);

      const ffDiag = res.ff
        ? (res.ff.ok
          ? `<span style="color: #10b981; font-weight: 600;">✓ ${res.ff.moboFF} in ${res.ff.caseFF} Case</span>`
          : `<span style="color: #ef4444; font-weight: 600;">✕ ${esc(res.ff.short)}</span>`)
        : (build.motherboard || build.case
          ? `<span style="color: #64748b;">${inferFormFactor(build.motherboard) || inferFormFactor(build.case) || 'Form Factor OK'}</span>`
          : `<span style="color: #94a3b8;">Pending Case & Motherboard</span>`);

      const psuDiag = res.psu
        ? (res.psu.ok
          ? (res.psu.isWarning
            ? `<span style="color: #d97706; font-weight: 600;">⚠️ ${res.psu.psuWatts}W (Headroom Tight)</span>`
            : `<span style="color: #10b981; font-weight: 600;">✓ ${res.psu.psuWatts}W (Headroom OK)</span>`)
          : `<span style="color: #ef4444; font-weight: 600;">✕ ${esc(res.psu.short)}</span>`)
        : (res.power && res.power.recommended
          ? `<span style="color: #64748b;">Requires ${res.power.recommended}W+ PSU</span>`
          : `<span style="color: #94a3b8;">Pending Components</span>`);

      const printHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>iGeek PC Build Quotation - ${buildId}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm 12mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #0f172a; background: #ffffff; padding: 18px 22px; font-size: 12px; line-height: 1.4; }
    .header-wrap { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 3px solid #0090d0; margin-bottom: 12px; }
    .brand-box { display: flex; align-items: center; gap: 12px; }
    .logo-img { width: 52px; height: auto; object-fit: contain; }
    .store-title { font-size: 22px; font-weight: 900; color: #0f172a; letter-spacing: -0.5px; margin-bottom: 2px; }
    .store-tagline { font-size: 11px; color: #64748b; }
    .meta-box { text-align: right; }
    .badge { display: inline-block; background: #0090d0; color: #ffffff; font-size: 10px; font-weight: 800; letter-spacing: 0.5px; padding: 3px 8px; border-radius: 4px; margin-bottom: 6px; text-transform: uppercase; }
    .meta-row { font-size: 11px; color: #334155; margin-bottom: 2px; }
    .contact-strip { display: flex; justify-content: space-between; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 6px 12px; font-size: 10.5px; color: #475569; margin-bottom: 14px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
    th { background: #0f172a; color: #ffffff; padding: 8px 12px; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; text-align: left; }
    .grid-wrap { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 14px; margin-bottom: 14px; }
    .card-box { border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 14px; background: #f8fafc; }
    .card-title { font-size: 11.5px; font-weight: 800; color: #0f172a; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #cbd5e1; text-transform: uppercase; letter-spacing: 0.3px; }
    .row-item { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 4px; color: #334155; }
    .total-row { margin-top: 8px; padding-top: 6px; border-top: 2px solid #0f172a; font-size: 13.5px; font-weight: 800; color: #0f172a; }
    .total-val { color: #0090d0; font-size: 15px; }
    .footer-wrap { display: flex; justify-content: space-between; align-items: flex-end; border-top: 1px solid #e2e8f0; padding-top: 10px; gap: 14px; }
    .terms { flex: 1; font-size: 9.5px; color: #64748b; line-height: 1.35; }
    .stamp { border: 2px dashed #0090d0; border-radius: 8px; padding: 6px 10px; color: #0090d0; text-align: center; font-weight: 800; font-size: 10px; text-transform: uppercase; transform: rotate(-3deg); }
    .stamp small { display: block; font-size: 8px; color: #10b981; }
  </style>
</head>
<body>
  <div class="header-wrap">
    <div class="brand-box">
      <img src="https://igeekjo.com/cdn/shop/files/igeek-logo---shimmer.gif" alt="iGeek Megastore" class="logo-img">
      <div>
        <h1 class="store-title">iGEEK MEGASTORE</h1>
        <p class="store-tagline">Custom PC Gaming & High-Performance Workstation Spec Sheet</p>
      </div>
    </div>
    <div class="meta-box">
      <div class="badge">Official Quotation</div>
      <div class="meta-row"><strong>Build ID:</strong> ${buildId}</div>
      <div class="meta-row"><strong>Date:</strong> ${dateStr} ${timeStr}</div>
      <div class="meta-row"><strong>Validity:</strong> 7 Days</div>
    </div>
  </div>

  <div class="contact-strip">
    <div><strong>Gardens:</strong> Wasfi At-Tall St. (06 5511105 / 079 5911003)</div>
    <div><strong>Sweifieh:</strong> Near Alsufara' Bakery (06 5511104 / 079 5911007)</div>
    <div><strong>5B Mall:</strong> Al-Quds St. (079 5911005)</div>
    <div><strong>Web:</strong> www.igeekjo.com | info@igeekjo.com</div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width: 20%;">Component</th>
        <th style="width: 50%;">Product Description & Hardware Specs</th>
        <th style="width: 10%; text-align: center;">Qty</th>
        <th style="width: 20%; text-align: right;">Price (JOD)</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
  </table>

  <div class="grid-wrap">
    <div class="card-box">
      <div class="card-title">Technical Diagnostics & Compatibility</div>
      <div class="row-item"><span>Compatibility Status:</span> <div>${compatStatusHtml}</div></div>
      <div class="row-item"><span>CPU & Motherboard Socket:</span> <div>${sockDiag}</div></div>
      <div class="row-item"><span>Memory Generation:</span> <div>${memDiag}</div></div>
      <div class="row-item"><span>Case & Motherboard Size:</span> <div>${ffDiag}</div></div>
      <div class="row-item"><span>Power Delivery (PSU):</span> <div>${psuDiag}</div></div>
      <div class="row-item"><span>Estimated Peak Draw:</span> <strong>${drawText}</strong></div>
      <div class="row-item"><span>Recommended PSU Capacity:</span> <strong>${psuText}</strong></div>
      <div style="margin-top: 6px; font-size: 9.5px; color: #64748b; font-style: italic;">
        * Socket, RAM, form factor & power verified. Physical clearances (GPU length, cooler/radiator clearance) & BIOS revision are verified by iGeek technicians prior to assembly.
      </div>
    </div>

    <div class="card-box">
      <div class="card-title">Quotation Summary</div>
      <div class="row-item"><span>Hardware Subtotal:</span> <span>${totalFormatted}</span></div>
      <div class="row-item"><span>Professional Assembly & Cable Routing:</span> <span style="color: #10b981; font-weight: 600;">0.00 JOD (FREE)</span></div>
      <div class="row-item"><span>BIOS Setup & 24h Stress Testing:</span> <span style="color: #10b981; font-weight: 600;">0.00 JOD (FREE)</span></div>
      <div class="row-item total-row">
        <span>Grand Total (Tax Included):</span>
        <span class="total-val">${totalFormatted}</span>
      </div>
    </div>
  </div>

  <div class="footer-wrap">
    <div class="terms">
      <p><strong>Warranty & Service Notes:</strong> All components are brand new with official agency warranties. Quotation valid for 7 days from date of issue. Professionally assembled, tuned, and tested at iGeek Megastore Amman.</p>
    </div>
    <div class="stamp">
      iGeek Megastore
      <small>Verified Build</small>
    </div>
  </div>
</body>
</html>`;

      const printWin = window.open('', '_blank', 'width=900,height=950');
      if (printWin) {
        printWin.document.open();
        printWin.document.write(printHtml);
        printWin.document.close();
        printWin.focus();
        setTimeout(function () {
          printWin.print();
        }, 350);
      } else {
        window.print();
      }
    }

    // Keyboard Accessibility & Focus Trapping
    document.addEventListener('keydown', e => {
      if (!state.openSlot) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
        return;
      }

      if (e.key === 'Tab') {
        const sheet = $('[data-pcb-sheet]');
        if (!sheet || sheet.hidden) return;
        const focusable = sheet.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    });

    // Global Click Event Handler
    document.addEventListener('click', e => {
      const chooseBtn = e.target.closest('[data-choose]');
      if (chooseBtn) {
        e.preventDefault();
        openDrawer(chooseBtn.dataset.choose);
        return;
      }

      const changeBtn = e.target.closest('[data-change]');
      if (changeBtn) {
        e.preventDefault();
        openDrawer(changeBtn.dataset.change);
        return;
      }

      const removeBtn = e.target.closest('[data-remove]');
      if (removeBtn) {
        e.preventDefault();
        const [key, idx] = removeBtn.dataset.remove.split(':');
        removePick(key, parseInt(idx, 10));
        return;
      }

      const pickBtn = e.target.closest('[data-pick]');
      if (pickBtn && state.openSlot) {
        e.preventDefault();
        if (pickBtn.disabled) return;
        const vid = pickBtn.dataset.pick;
        const items = state.pool[state.openSlot.handle] || [];
        const item = items.find(i => String(i.vid) === vid);
        if (item && item.avail !== false) {
          setPick(state.openSlot.key, item);
          closeDrawer();
        }
        return;
      }

      const candidateRow = e.target.closest('.pcb-candidate-row:not(.is-incompatible):not(.is-sold-out)');
      if (candidateRow && !e.target.closest('button') && state.openSlot) {
        const btn = candidateRow.querySelector('[data-pick]:not([disabled])');
        if (btn) {
          e.preventDefault();
          const vid = btn.dataset.pick;
          const items = state.pool[state.openSlot.handle] || [];
          const item = items.find(i => String(i.vid) === vid);
          if (item && item.avail !== false) {
            setPick(state.openSlot.key, item);
            closeDrawer();
          }
          return;
        }
      }

      if (e.target.closest('[data-pcb-reset-filter]')) {
        e.preventDefault();
        state.compatOnly = false;
        state.inStockOnly = false;
        state.searchQuery = '';
        const cBox = $('[data-pcb-compat-only]');
        const iBox = $('[data-pcb-instock-only]');
        const sBox = $('[data-pcb-search]');
        if (cBox) cBox.checked = false;
        if (iBox) iBox.checked = false;
        if (sBox) sBox.value = '';
        renderDrawerItems();
        return;
      }

      if (e.target.closest('[data-pcb-sheet-close]') || e.target.closest('[data-pcb-scrim]')) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      if (e.target.closest('[data-pcb-bar-status]')) {
        e.preventDefault();
        const overviewEl = $('[data-pcb-status]') || $('[data-pcb-total]');
        if (overviewEl) {
          overviewEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      if (e.target.closest('[data-pcb-add], [data-pcb-add-mobile]')) {
        e.preventDefault();
        handleAddToCart();
        return;
      }

      if (e.target.closest('[data-pcb-share]')) {
        e.preventDefault();
        handleShareBuild();
        return;
      }

      if (e.target.closest('[data-pcb-reset]')) {
        e.preventDefault();
        if (confirm('Clear current build selections?')) {
          state.cfg.slots.forEach(s => {
            state.picked[s.key] = s.max > 1 ? [] : null;
          });
          state.buildId = null;
          // Clear share URL query param
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('build');
          window.history.replaceState(null, '', cleanUrl.toString());
          renderApp();
          showToast('Build reset');
        }
        return;
      }

      if (e.target.closest('[data-pcb-print]')) {
        e.preventDefault();
        printBuildSpecSheet();
        return;
      }
    });

    const searchInput = $('[data-pcb-search]');
    if (searchInput) {
      searchInput.addEventListener('input', e => {
        state.searchQuery = e.target.value;
        renderDrawerItems();
      });
    }

    const compatToggle = $('[data-pcb-compat-only]');
    if (compatToggle) {
      compatToggle.addEventListener('change', e => {
        state.compatOnly = e.target.checked;
        renderDrawerItems();
      });
    }

    const inStockToggle = $('[data-pcb-instock-only]');
    if (inStockToggle) {
      inStockToggle.addEventListener('change', e => {
        state.inStockOnly = e.target.checked;
        renderDrawerItems();
      });
    }

    renderApp();
    prefetchSlots();
    restoreBuildFromUrl();
  }

  function start() {
    const el = document.querySelector('[data-pcb]');
    if (el) initPCBuilder(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  window.addEventListener('load', start);

})();
